import { useCallback, useRef, useState } from 'react'
import { useStore } from 'react-redux'

import { ArtifactExportType } from '@/analytics/enums/artifact-template.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackArtifactDownloadFailed, trackArtifactDownloadSuccess } from '@/analytics/utils/artifact-download-analytics'
import { generateDefaultDocxFilename } from '@/agent-v2/artifact-templates/utils/generate-default-filename'
import { uploadBlobToUserFiles } from '@/content-viewer/components/common/save-to-user-files-modal/actions/save-artifact-to-user-files'
import { GlobalToastType, kToastDurationMediumMs, kToastDurationShortMs } from '@/constants/constants-ui'
import { kUntitledDraftName } from '@/drafting/constants'
import { getCurrentDraftVersion } from '@/drafting/destination/utils/get-current-draft-version'
import { DraftingActions } from '@/drafting/store/slice'
import type { MatterArtifactDraftListRow } from '@/drafting/hooks/useMatterArtifactDrafts'
import { downloadMatterArtifactAsDocx, fetchMatterArtifactDocxBlob } from '@/matter-artifacts/fetch/download-matter-artifact-docx'
import type { MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { matterPaths } from '@/routing/paths'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import type { RootState } from '@/store/store'
import { useAppDispatch } from '@/store/store-hooks'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { getOrFetchRootFolderId } from '@/user_files/folders/utils/get-or-fetch-root-folder-id'

/** What a row's export does with the document it fetches. */
export enum DraftExportAction {
  /** Delivers the file to the browser. */
  DOWNLOAD = 'download',
  /** Uploads the file into the matter's files as a new document. */
  SAVE_TO_MATTER_FILES = 'save-to-matter-files',
}

export interface UseExportDraftResult {
  /** Downloads a draft as DOCX. A row already exporting ignores further calls. */
  downloadDraft: (row: MatterArtifactDraftListRow) => Promise<void>
  /** Saves a draft into the matter's files as a new DOCX. A row already exporting ignores further calls. */
  saveDraftToMatterFiles: (row: MatterArtifactDraftListRow) => Promise<void>
  /**
   * Matter artifact ids whose export is in flight.
   *
   * Keyed on the artifact rather than the version: the list polls, so a collaborator's save can give
   * a row a new version id part-way through an export. A version-keyed lock would lose track of the
   * running export, drop the row's spinner and let a second one start.
   */
  exportingMatterArtifactIds: readonly string[]
}

/**
 * Exporting a draft from its list row, as a download or as a copy in the matter's files.
 *
 * The list has no template control, so an export here carries no template formatting: it is the
 * document as it stands, whichever destination it goes to. Exports are tracked per row, so one
 * draft exporting never blocks another, and a row runs one at a time — its menu trigger is the
 * single control, and shows that export's progress.
 */
export function useExportDraft(matterId: string | null): UseExportDraftResult {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const { trackEvent } = useAnalytics()

  // The ref is the guard and the state is what renders: a second click has to be rejected before
  // React has re-rendered the first one's spinner.
  const inFlightMatterArtifactIdsRef = useRef<Set<string>>(new Set())
  const [exportingMatterArtifactIds, setExportingMatterArtifactIds] = useState<readonly string[]>([])

  const runExport = useCallback(
    async (row: MatterArtifactDraftListRow, action: DraftExportAction) => {
      if (inFlightMatterArtifactIdsRef.current.has(row.matterArtifactId)) return
      inFlightMatterArtifactIdsRef.current.add(row.matterArtifactId)
      setExportingMatterArtifactIds([...inFlightMatterArtifactIdsRef.current])

      let browserDownloadVersionId: string | undefined
      try {
        if (!matterId) throw new Error('No matter to export this draft from')

        // The list is a cache, so the version id on the row is only as current as the last fetch.
        // Resolving the draft's head here is what keeps an export started from the row from
        // delivering a colleague's pre-edit document.
        const currentVersion = await getCurrentDraftVersion(matterId, row.matterArtifactId)

        // Another member has deleted the draft since the list was fetched. Nothing failed and there
        // is nothing to retry, so say what happened rather than offering the export's error, and
        // invalidate the list so its own refresh takes the row away. The row's absence needs the
        // explanation: it disappears a moment after the toast.
        if (!currentVersion) {
          dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))
          dispatch(openGlobalToast({ type: GlobalToastType.INFO, message: 'This draft has been deleted.', durationMs: kToastDurationMediumMs }))
          return
        }

        // A collaborator has saved an edit since the list was fetched. The export uses the new
        // version either way; invalidating brings the row's name and Last Modified up to date with it.
        if (currentVersion.id !== row.artifactVersionId) dispatch(DraftingActions.invalidateMatterDrafts({ matterId }))

        if (action === DraftExportAction.DOWNLOAD) {
          browserDownloadVersionId = currentVersion.id
          const filename = await downloadMatterArtifactAsDocx(currentVersion.id)
          dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${filename}`, durationMs: kToastDurationShortMs }))
          trackArtifactDownloadSuccess(trackEvent, { artifactVersionId: currentVersion.id, artifactId: row.matterArtifactId })
        } else {
          await saveCopyToMatterFiles(currentVersion, matterId, store.getState, dispatch)
        }

        trackEvent(AnalyticsEvent.ArtifactExportCompleted, {
          artifactVersionId: currentVersion.id,
          artifactId: row.matterArtifactId,
          artifactExportType: action === DraftExportAction.DOWNLOAD ? ArtifactExportType.Download : ArtifactExportType.SaveToMatter,
          templateApplied: false,
          conversationId: row.conversationId,
          matterId,
        })
      } catch {
        dispatch(
          openGlobalToast({
            type: GlobalToastType.ERROR,
            message: action === DraftExportAction.DOWNLOAD ? 'Download failed. Please try again.' : 'Save to matter files failed. Please try again.',
            durationMs: kToastDurationMediumMs,
          })
        )
        if (browserDownloadVersionId) {
          trackArtifactDownloadFailed(trackEvent, { artifactVersionId: browserDownloadVersionId, artifactId: row.matterArtifactId })
        }
      } finally {
        inFlightMatterArtifactIdsRef.current.delete(row.matterArtifactId)
        setExportingMatterArtifactIds([...inFlightMatterArtifactIdsRef.current])
      }
    },
    [dispatch, matterId, store, trackEvent]
  )

  const downloadDraft = useCallback((row: MatterArtifactDraftListRow) => runExport(row, DraftExportAction.DOWNLOAD), [runExport])
  const saveDraftToMatterFiles = useCallback((row: MatterArtifactDraftListRow) => runExport(row, DraftExportAction.SAVE_TO_MATTER_FILES), [runExport])

  return { downloadDraft, saveDraftToMatterFiles, exportingMatterArtifactIds }
}

/**
 * File a copy of the draft in the matter's top-level files and point the user at it.
 *
 * The copy is a new document every time, so a repeated save of one draft leaves the earlier copies
 * alone. `matterId` and the folder are read before the upload and used throughout, so a matter
 * switch part-way through cannot move the copy or the link that reports it. The name comes from the
 * same resolved head as the content, so the copy is not filed under a title the draft has outgrown.
 */
async function saveCopyToMatterFiles(
  currentVersion: MatterArtifactVersionRead,
  matterId: string,
  getState: () => RootState,
  dispatch: ReturnType<typeof useAppDispatch>
): Promise<void> {
  // Resolved before the document is fetched, so a matter whose files cannot be reached fails here
  // rather than after. It waits on a root folder still loading and rejects on one that cannot be
  // read, so the action never quietly does nothing.
  const rootFolderId = await getOrFetchRootFolderId({ dispatch, getState, matterId })

  const { blob } = await fetchMatterArtifactDocxBlob(currentVersion.id)
  await uploadBlobToUserFiles(blob, generateDefaultDocxFilename(currentVersion.name ?? kUntitledDraftName), rootFolderId, matterId, dispatch)

  dispatch(UserFilesActions.fetchFilesForFolder({ folderId: rootFolderId }))
  dispatch(
    openGlobalToast({
      type: GlobalToastType.SUCCESS,
      message: 'Saved to matter files.',
      durationMs: kToastDurationShortMs,
      withLink: { label: 'View files', href: matterPaths.userFiles.folder(matterId, rootFolderId) },
    })
  )
}
